// Original schematic line-art illustrations for the New Ballard Score
// neuromuscular maturity criteria. Drawn from scratch — NOT traced from any
// published chart. Each figure emphasises the joint angle relevant to the
// criterion; not anatomically detailed on purpose (schematic).
//
// Score domains follow the app spec: -1 to 4 for all six neuromuscular criteria.

import React from "react";

const STROKE = "#1c1917"; // stone-900
const ACCENT = "#0369a1"; // sky-700
const MUTED = "#a8a29e"; // stone-400

// Helper — draw a body outline stub (torso + head) used as backdrop
function Torso() {
  return (
    <>
      {/* head */}
      <circle cx="50" cy="18" r="7" fill="none" stroke={STROKE} strokeWidth="1.5" />
      {/* torso */}
      <path d="M42 26 Q42 42 44 60 L56 60 Q58 42 58 26 Z" fill="none" stroke={STROKE} strokeWidth="1.5" />
    </>
  );
}

// ---------- POSTURE ----------
// Score 0..4: increasing flexion of arms and legs (supine, top-down view)
// -1 = same as 0 (very extended)
function Posture({ score }) {
  // Map score -1..4 into arm/leg angle in degrees from body axis.
  // 0 = fully extended (arms/legs straight out), 4 = full flexion.
  const table = {
    "-1": { armBend: 0, legBend: 0 },
    0: { armBend: 5, legBend: 5 },
    1: { armBend: 20, legBend: 15 },
    2: { armBend: 40, legBend: 30 },
    3: { armBend: 70, legBend: 55 },
    4: { armBend: 90, legBend: 80 },
  };
  const { armBend, legBend } = table[String(score)];
  // Supine baby, top-down. Draw two-segment arms and legs with bend.
  const arm = (side) => {
    const sx = side === "L" ? 42 : 58;
    const dir = side === "L" ? -1 : 1;
    // shoulder → elbow (out to the side)
    const ex = sx + dir * 10;
    const ey = 34;
    // elbow → hand: rotates toward torso as bend increases
    const rad = (armBend * Math.PI) / 180;
    const hx = ex + dir * 10 * Math.cos(rad) * -1 + dir * (armBend > 40 ? 0 : 4);
    const hy = ey + 10 * Math.sin(rad);
    return (
      <g key={`arm-${side}`}>
        <line x1={sx} y1="30" x2={ex} y2={ey} stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
        <line x1={ex} y1={ey} x2={hx} y2={hy} stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" />
      </g>
    );
  };
  const leg = (side) => {
    const sx = side === "L" ? 46 : 54;
    const dir = side === "L" ? -1 : 1;
    const kx = sx + dir * 4;
    const ky = 78;
    const rad = (legBend * Math.PI) / 180;
    const fx = kx + dir * 8 * Math.cos(rad) * -1;
    const fy = ky + 14 * Math.cos(rad / 2);
    return (
      <g key={`leg-${side}`}>
        <line x1={sx} y1="60" x2={kx} y2={ky} stroke={STROKE} strokeWidth="1.5" strokeLinecap="round" />
        <line x1={kx} y1={ky} x2={fx} y2={fy} stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" />
      </g>
    );
  };
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      <Torso />
      {arm("L")}
      {arm("R")}
      {leg("L")}
      {leg("R")}
    </svg>
  );
}

// ---------- SQUARE WINDOW (wrist) ----------
// Angle between palm and forearm.
// -1 = >90°  0 = 90°  1 = 60°  2 = 45°  3 = 30°  4 = 0°
function SquareWindow({ score }) {
  const angleTable = { "-1": 105, 0: 90, 1: 60, 2: 45, 3: 30, 4: 0 };
  const angle = angleTable[String(score)];
  // Forearm is horizontal. Hand pivots at wrist (x=70,y=50), palm at angle above.
  const rad = (angle * Math.PI) / 180;
  const wx = 70;
  const wy = 50;
  const hx = wx - 22 * Math.cos(rad);
  const hy = wy - 22 * Math.sin(rad);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      {/* forearm */}
      <line x1="15" y1="50" x2={wx} y2={wy} stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
      {/* palm */}
      <line x1={wx} y1={wy} x2={hx} y2={hy} stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
      {/* fingers hint */}
      <line
        x1={hx}
        y1={hy}
        x2={hx - 3 * Math.cos(rad + 0.5)}
        y2={hy - 3 * Math.sin(rad + 0.5)}
        stroke={ACCENT}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      {/* angle arc */}
      <path
        d={`M ${wx - 12} ${wy} A 12 12 0 0 1 ${wx - 12 * Math.cos(rad)} ${wy - 12 * Math.sin(rad)}`}
        fill="none"
        stroke={MUTED}
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <text x={wx - 22} y={wy - 4} fontSize="8" fill={MUTED} fontFamily="monospace">
        {angle}°
      </text>
    </svg>
  );
}

// ---------- ARM RECOIL ----------
// Angle to which flexed arm springs back after brief extension.
// -1 = 180° (no recoil)  0 = 140–180°  1 = 110–140°  2 = 90–110°  3 = <90°  4 = tight <90°
function ArmRecoil({ score }) {
  const angleTable = { "-1": 175, 0: 155, 1: 125, 2: 100, 3: 80, 4: 45 };
  const angle = angleTable[String(score)];
  // Upper arm going down from shoulder (30,25) to elbow (30,55).
  // Forearm rotates outward — angle measured between upper arm and forearm.
  const sx = 30,
    sy = 25;
  const ex = 30,
    ey = 55;
  const rad = ((180 - angle) * Math.PI) / 180; // deviation from straight down
  const hx = ex + 28 * Math.sin(rad);
  const hy = ey + 28 * Math.cos(rad);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      {/* shoulder blob */}
      <circle cx={sx} cy={sy} r="3" fill={STROKE} />
      {/* upper arm */}
      <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
      {/* forearm */}
      <line x1={ex} y1={ey} x2={hx} y2={hy} stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
      {/* angle arc */}
      <path
        d={`M ${ex} ${ey + 12} A 12 12 0 0 0 ${ex + 12 * Math.sin(rad)} ${ey + 12 * Math.cos(rad)}`}
        fill="none"
        stroke={MUTED}
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <text x={ex + 4} y={ey + 20} fontSize="8" fill={MUTED} fontFamily="monospace">
        {angle}°
      </text>
    </svg>
  );
}

// ---------- POPLITEAL ANGLE ----------
// Angle behind the knee with hip flexed at 90°.
// -1 = 180°  0 = 160°  1 = 140°  2 = 120°  3 = 100°  4 = 90°
function PoplitealAngle({ score }) {
  const angleTable = { "-1": 180, 0: 160, 1: 140, 2: 120, 3: 100, 4: 90 };
  const angle = angleTable[String(score)];
  // Thigh horizontal from hip (20,55) to knee (60,55).
  // Lower leg extends from knee; angle measured behind knee (above thigh line).
  const hipX = 20,
    hipY = 55;
  const kneeX = 60,
    kneeY = 55;
  // The interior angle (opening upward). Convert to direction of lower leg.
  // If angle = 180°, leg continues horizontal. If 90°, leg goes straight up.
  const rad = ((180 - angle) * Math.PI) / 180;
  const footX = kneeX + 32 * Math.cos(rad);
  const footY = kneeY - 32 * Math.sin(rad);
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      {/* hip blob */}
      <circle cx={hipX} cy={hipY} r="3" fill={STROKE} />
      {/* thigh */}
      <line x1={hipX} y1={hipY} x2={kneeX} y2={kneeY} stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
      {/* lower leg */}
      <line x1={kneeX} y1={kneeY} x2={footX} y2={footY} stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
      {/* foot hint */}
      <circle cx={footX} cy={footY} r="2.5" fill={ACCENT} />
      {/* angle arc */}
      <path
        d={`M ${kneeX - 12} ${kneeY} A 12 12 0 0 0 ${kneeX + 12 * Math.cos(rad)} ${kneeY - 12 * Math.sin(rad)}`}
        fill="none"
        stroke={MUTED}
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <text x={kneeX - 8} y={kneeY - 15} fontSize="8" fill={MUTED} fontFamily="monospace">
        {angle}°
      </text>
    </svg>
  );
}

// ---------- SCARF SIGN ----------
// How far the elbow can be drawn across the chest when arm pulled across body.
// -1 = elbow reaches past opposite shoulder (very lax)
// 0 = at opposite axillary line
// 1 = at opposite midclavicular line
// 2 = at midline (sternum)
// 3 = between midline & ipsilateral nipple
// 4 = does not reach midline (elbow at ipsilateral side)
function ScarfSign({ score }) {
  // Draw a chest (a rectangle) with a shoulder on right (60,20) and
  // show where elbow lands — X coordinate on chest.
  const elbowXTable = { "-1": 15, 0: 25, 1: 35, 2: 45, 3: 55, 4: 65 };
  const elbowX = elbowXTable[String(score)];
  // shoulder right side
  const shX = 70,
    shY = 22;
  // hand near opposite side
  const handX = elbowX - 12;
  const handY = 60;
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      {/* chest outline */}
      <rect x="15" y="18" width="70" height="60" rx="8" fill="none" stroke={STROKE} strokeWidth="1.5" />
      {/* midline */}
      <line x1="50" y1="18" x2="50" y2="78" stroke={MUTED} strokeWidth="0.8" strokeDasharray="3 3" />
      {/* shoulder joint */}
      <circle cx={shX} cy={shY} r="3" fill={STROKE} />
      {/* upper arm shoulder -> elbow */}
      <line x1={shX} y1={shY} x2={elbowX} y2="40" stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
      {/* forearm elbow -> hand */}
      <line x1={elbowX} y1="40" x2={handX} y2={handY} stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
      {/* elbow marker */}
      <circle cx={elbowX} cy="40" r="3" fill={ACCENT} />
      <text x={elbowX - 3} y="34" fontSize="7" fill={MUTED} fontFamily="monospace">
        elbow
      </text>
    </svg>
  );
}

// ---------- HEEL TO EAR ----------
// How close the heel can be brought to the ear (with knee to side).
// -1 = heel touches ear  0 = near ear  1 = eye level  2 = nipple  3 = umbilicus  4 = past knee
function HeelToEar({ score }) {
  // Draw supine baby side profile: head at top, torso extending down.
  // Foot Y position varies with score. Small score = foot high (near head).
  const footYTable = { "-1": 15, 0: 22, 1: 32, 2: 45, 3: 60, 4: 78 };
  const footY = footYTable[String(score)];
  const footX = 40;
  // Hip at (55, 55), knee out to side, foot at (footX, footY)
  const hipX = 55,
    hipY = 55;
  const kneeX = 30,
    kneeY = (hipY + footY) / 2;
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
      {/* head */}
      <circle cx="72" cy="18" r="8" fill="none" stroke={STROKE} strokeWidth="1.5" />
      {/* ear */}
      <circle cx="64" cy="18" r="1.5" fill={STROKE} />
      {/* torso */}
      <path d="M62 26 Q60 45 60 65 L74 65 Q76 45 78 26 Z" fill="none" stroke={STROKE} strokeWidth="1.5" />
      {/* thigh */}
      <line x1={hipX} y1={hipY} x2={kneeX} y2={kneeY} stroke={STROKE} strokeWidth="3" strokeLinecap="round" />
      {/* lower leg */}
      <line x1={kneeX} y1={kneeY} x2={footX} y2={footY} stroke={ACCENT} strokeWidth="3" strokeLinecap="round" />
      {/* foot marker */}
      <circle cx={footX} cy={footY} r="3" fill={ACCENT} />
      {/* dashed guide from foot to ear */}
      <line x1={footX} y1={footY} x2="64" y2="18" stroke={MUTED} strokeWidth="0.8" strokeDasharray="2 3" />
    </svg>
  );
}

// ---------- Dispatcher ----------
export function NeuromuscularIllustration({ criterion, score }) {
  switch (criterion) {
    case "posture":
      return <Posture score={score} />;
    case "square_window":
      return <SquareWindow score={score} />;
    case "arm_recoil":
      return <ArmRecoil score={score} />;
    case "popliteal":
      return <PoplitealAngle score={score} />;
    case "scarf":
      return <ScarfSign score={score} />;
    case "heel_ear":
      return <HeelToEar score={score} />;
    default:
      return null;
  }
}
